"use client"

import { useEffect, useState } from "react"
import { IoChevronBack, IoChevronForward } from "react-icons/io5"

/**
 * DatePicker Component - A customizable date picker with calendar interface
 * 
 * Props:
 * @param {string} value - Selected date in "YYYY-MM-DD" format (e.g., "2024-01-15")
 * @param {function} onChange - Callback function called when date is selected (receives date string)
 * @param {string} minDate - Minimum selectable date in "YYYY-MM-DD" format (e.g., "2024-01-01")
 * @param {string} maxDate - Maximum selectable date in "YYYY-MM-DD" format (e.g., "2024-12-31")
 * @param {boolean} disabled - Whether the picker is disabled (default: false)
 * @param {string} className - Additional CSS classes for styling
 * @param {string} placeholder - Placeholder text when no date is selected (default: "Select date")
 * 
 * Usage Example:
 * <DatePicker 
 *   value={selectedDate} 
 *   onChange={(date) => setSelectedDate(date)}
 *   minDate="2024-01-01"
 *   maxDate="2024-12-31"
 *   placeholder="Choose a date"
 * />
 */
export function DatePicker({
  value = "",
  onChange,
  minDate,
  maxDate,
  disabled = false,
  className = "",
  placeholder = "Select date",
  isDarkMode = false,
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedDate, setSelectedDate] = useState(null)

  // Parse initial value
  useEffect(() => {
    if (value) {
      const date = new Date(value)
      if (!isNaN(date.getTime())) {
        setSelectedDate(date)
        setCurrentDate(new Date(date.getFullYear(), date.getMonth(), 1))
      }
    }
  }, [value])

  const today = new Date()
  const currentYear = currentDate.getFullYear()
  const currentMonth = currentDate.getMonth()

  // Get days in month
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate()
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay()

  // Generate calendar days
  const calendarDays = []

  // Previous month's trailing days
  const prevMonth = new Date(currentYear, currentMonth - 1, 0)
  for (let i = firstDayOfMonth - 1; i >= 0; i--) {
    calendarDays.push({
      day: prevMonth.getDate() - i,
      isCurrentMonth: false,
      date: new Date(currentYear, currentMonth - 1, prevMonth.getDate() - i),
    })
  }

  // Current month days
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push({
      day,
      isCurrentMonth: true,
      date: new Date(currentYear, currentMonth, day),
    })
  }

  // Next month's leading days
  const remainingDays = 35 - calendarDays.length // 5 rows × 7 days
  for (let day = 1; day <= remainingDays; day++) {
    calendarDays.push({
      day,
      isCurrentMonth: false,
      date: new Date(currentYear, currentMonth + 1, day),
    })
  }

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ]

  const dayNames = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"]

  // Helper function to format date as YYYY-MM-DD without timezone issues
  const formatDateString = (date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const navigateMonth = (direction) => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev)
      if (direction === "prev") {
        newDate.setMonth(prev.getMonth() - 1)
      } else {
        newDate.setMonth(prev.getMonth() + 1)
      }
      return newDate
    })
  }

  const selectDate = (date) => {
    const dateString = formatDateString(date)

    // Check if date is within allowed range
    if (minDate && dateString < minDate) return
    if (maxDate && dateString > maxDate) return

    setSelectedDate(date)
    if (onChange) {
      onChange(dateString)
    }
    setIsOpen(false)
  }

  const isDateDisabled = (date) => {
    const dateString = formatDateString(date)
    
    if (minDate && dateString < minDate) return true
    if (maxDate && dateString > maxDate) return true
    return false
  }

  const isDateSelected = (date) => {
    if (!selectedDate) return false
    return date.toDateString() === selectedDate.toDateString()
  }

  const isToday = (date) => {
    return date.toDateString() === today.toDateString()
  }

  const formatDisplayDate = (date) => {
    if (!date) return ""
    return date.toLocaleDateString("en-GB") // DD/MM/YYYY format
  }

  const setToday = () => {
    if (!isDateDisabled(today)) {
      selectDate(today)
    }
  }

  const clearDate = () => {
    setSelectedDate(null)
    if (onChange) {
      onChange("")
    }
    setIsOpen(false)
  }

  return (
    <>
      <style jsx>{`
        @keyframes windowsMinimizeIn {
          0% {
            opacity: 0;
            transform: translateY(-15px) scale(0.9);
            transform-origin: bottom center;
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        @keyframes windowsMinimizeOut {
          0% {
            opacity: 1;
            transform: translateY(0) scale(1);
            transform-origin: bottom center;
          }
          100% {
            opacity: 0;
            transform: translateY(-15px) scale(0.9);
          }
        }
        
        @keyframes overlayFadeIn {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        
        @keyframes overlayFadeOut {
          0% { opacity: 1; }
          100% { opacity: 0; }
        }
        
        .date-picker-overlay {
          animation: overlayFadeIn 0.1s ease-out forwards;
        }
        
        .date-picker-overlay.closing {
          animation: overlayFadeOut 0.1s ease-out forwards;
        }
      `}</style>
      <div className={`relative ${className} `}>
      <input
        type="text"
        value={formatDisplayDate(selectedDate)}
        placeholder={placeholder}
        readOnly
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`${className} custom-input-padding`}
        style={{
          width: '100%',
          height: '42px',
          padding: '0.5rem !important',
          paddingLeft: '0.5rem !important',
          paddingRight: '0.5rem !important',
          paddingTop: '0.5rem !important',
          paddingBottom: '0.5rem !important',
          border: '0.5px solid #e6e6e6',
          borderRadius: '5px',
          fontSize: '16px',
          fontFamily: 'Gilroy-Regular',
          backgroundColor: isDarkMode ? '#2a2a2a' : '#ffffff',
          color: isDarkMode ? '#ffffff' : '#000000',
          cursor: 'pointer',
          transform: 'translateZ(0)',
          WebkitTransform: 'translateZ(0)'
        }}
      />

      {isOpen && !disabled && (
        <div 
          className={`absolute bottom-full left-0 mb-1 border border-gray-300 rounded-md shadow-lg p-3 w-56 ${isDarkMode ? 'bg-[#2a2a2a] border-gray-600' : 'bg-white border-gray-300'}`} 
          style={{ 
            zIndex: 2147483647, 
            position: 'absolute',
            animation: 'windowsMinimizeIn 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards',
            transformOrigin: 'bottom center'
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <button
              type="button"
              onClick={() => navigateMonth("prev")}
              className={`p-1 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
            >
              <IoChevronBack className={`w-5 h-5 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} />
            </button>

            <h3 className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {monthNames[currentMonth]} {currentYear}
            </h3>

            <button
              type="button"
              onClick={() => navigateMonth("next")}
              className={`p-1 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
            >
              <IoChevronForward className={`w-5 h-5 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} />
            </button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {dayNames.map((day) => (
              <div key={day} className={`text-center text-xs font-medium py-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {day}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((calendarDay, index) => {
              const isDisabled = isDateDisabled(calendarDay.date)
              const isSelected = isDateSelected(calendarDay.date)
              const isTodayDate = isToday(calendarDay.date)

              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => !isDisabled && selectDate(calendarDay.date)}
                  disabled={isDisabled}
                  className={`
                    w-7 h-7 text-xs rounded-full flex items-center justify-center
                    ${!calendarDay.isCurrentMonth ? (isDarkMode ? "text-gray-500" : "text-gray-400") : (isDarkMode ? "text-white" : "text-gray-900")}
                    ${isSelected ? "bg-orange-500 text-white" : ""}
                    ${isTodayDate && !isSelected ? (isDarkMode ? "bg-orange-900 text-orange-300" : "bg-orange-100 text-orange-600") : ""}
                    ${isDisabled ? "opacity-50 cursor-not-allowed" : (isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100")}
                    ${!isDisabled && !isSelected ? (isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100") : ""}
                  `}
                >
                  {calendarDay.day}
                </button>
              )
            })}
          </div>

          {/* Footer buttons */}
          <div className={`flex justify-between mt-3 pt-2 border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
            <button
              type="button"
              onClick={setToday}
              className={`text-xs ${isDarkMode ? 'text-orange-400 hover:text-orange-300' : 'text-orange-600 hover:text-orange-700'}`}
            >
              Today
            </button>
            <button
              type="button"
              onClick={clearDate}
              className={`text-xs ${isDarkMode ? 'text-orange-400 hover:text-orange-300' : 'text-orange-600 hover:text-orange-700'}`}
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Overlay to close picker when clicking outside */}
      {isOpen && <div className="fixed inset-0 date-picker-overlay" style={{ zIndex: 2147483646 }} onClick={() => setIsOpen(false)} />}
      </div>
    </>
  )
}
